import { useBreakpoint } from "#/hooks/use-breakpoint";
import { useTStream } from "#/hooks/use-t-stream";
import { useVisualViewportHeight } from "#/hooks/use-visual-viewport-height";
import { hapticWarning } from "#/lib/haptics";
import { cn } from "#/lib/utils";
import { Button } from "#/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "#/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "#/ui/drawer";
import { Input } from "#/ui/input";
import clsx from "clsx";
import { TrashIcon } from "lucide-react";
import * as React from "react";
import { twMerge } from "tailwind-merge";

export const DrawerDialogBase = ({
  children,
  className,
  closeVisible = true,
  trigger,
  asChild = true,
  ...props
}: {
  open?: boolean;
  className?: string;
  onOpenChange?: (isOpen: boolean) => void;
  children: React.ReactNode;
  closeVisible?: boolean;
  trigger?: React.ReactNode;
  asChild?: boolean;
}) => {
  const isDesktop = useBreakpoint("md");
  const height = useVisualViewportHeight();
  const { t } = useTStream("common");

  if (isDesktop) {
    return (
      <Dialog {...props}>
        {trigger && <DialogTrigger asChild={asChild}>{trigger}</DialogTrigger>}
        <DialogContent
          closeVisible={closeVisible}
          className={twMerge("p-0 max-h-[95dvh] overflow-auto", className)}
        >
          <DialogHeader className="sr-only">
            <DialogTitle>Dialog</DialogTitle>
          </DialogHeader>
          {children}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer {...props}>
      <DrawerTitle className="sr-only">Dialog</DrawerTitle>
      {trigger && <DrawerTrigger asChild={asChild}>{trigger}</DrawerTrigger>}
      <DrawerContent>
        <div
          className="px-4 space-y-4 overflow-auto"
          style={{
            maxHeight: height - height / 3,
          }}
        >
          {children}
        </div>
        {closeVisible && (
          <DrawerFooter className="pt-2 mt-4">
            <DrawerClose asChild>
              <Button variant="outline">{t("Cancel")}</Button>
            </DrawerClose>
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export const DrawerDialog = ({
  children,
  title,
  description,
  right,
  classNameHeader,
  trigger,
  closeVisible = true,
  className,
  ...props
}: {
  description?: string | React.ReactNode;
  title: string | React.ReactNode;
  open?: boolean;
  right?: React.ReactNode;
  className?: string;
  classNameHeader?: string;
  closeVisible?: boolean;
  trigger?: React.ReactNode;
  onOpenChange?: (isOpen: boolean) => void;
  children: React.ReactNode;
}) => {
  const isDesktop = useBreakpoint("md");
  const height = useVisualViewportHeight();
  const { t } = useTStream("common");
  // Memoizing on [] froze the FIRST render's `onOpenChange` (`props` is a new
  // object every render), so the mobile Cancel button below called a stale
  // callback. Consumers whose handler closes over changing state — e.g. a
  // guard that refuses to close mid-run — had that guard silently bypassed.
  const { onOpenChange } = props;
  const onClose = React.useCallback(
    () => onOpenChange?.(false),
    [onOpenChange],
  );

  if (isDesktop) {
    return (
      <Dialog {...props}>
        {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent
          closeVisible={closeVisible}
          className={cn(
            "max-h-[95dvh] overflow-hidden flex flex-col",
            className,
          )}
        >
          <DialogHeader
            className={cn(
              "flex w-full items-center flex-row justify-between sticky top-0 z-10",
              classNameHeader,
              {
                "pr-3": !!right,
              },
            )}
          >
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-xl">{title}</DialogTitle>
              {description && (
                <DialogDescription className="text-left text-muted">
                  {description}
                </DialogDescription>
              )}
            </div>
            {right}
          </DialogHeader>
          <div className="overflow-auto flex-1">{children}</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer {...props}>
      {trigger && <DrawerTrigger asChild>{trigger}</DrawerTrigger>}
      <DrawerContent>
        <DrawerHeader
          className={cn(
            "text-left flex items-center flex-row justify-between sticky top-0 z-10 bg-card",
            classNameHeader,
            {
              "pr-0": !!right,
            },
          )}
        >
          <div className="text-left min-w-0 flex-1">
            <DrawerTitle>{title}</DrawerTitle>
            {description && (
              <DrawerDescription className="text-left text-muted">
                {description}
              </DrawerDescription>
            )}
          </div>
          {right}
        </DrawerHeader>
        <div
          className="px-4 space-y-4 overflow-x-hidden overflow-y-auto"
          style={{
            maxHeight: height - height / 3,
          }}
        >
          {children}
        </div>
        {closeVisible && (
          <DrawerFooter className="pt-2 mt-4">
            <DrawerClose asChild>
              <div>
                <Button variant="outline" onClick={onClose}>
                  {t("Cancel")}
                </Button>
              </div>
            </DrawerClose>
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export const DrawerDialogStacked = ({
  children,
  title,
  description,
  trigger,
  closeVisible = true,
  className,
  asChild = true,
  classNameIcon,
  icon,
  ...props
}: {
  description?: string | React.ReactNode;
  title: string;
  open?: boolean;
  closeVisible?: boolean;
  icon?: React.ReactNode;
  trigger?: React.ReactNode;
  classNameIcon?: string;
  className?: string;
  onOpenChange?: (isOpen: boolean) => void;
  children?: React.ReactNode;
  asChild?: boolean;
}) => {
  const { t } = useTStream("common");
  const isDesktop = useBreakpoint("md");
  const height = useVisualViewportHeight();

  if (isDesktop) {
    return (
      <Dialog {...props}>
        {trigger && <DialogTrigger asChild={asChild}>{trigger}</DialogTrigger>}
        <DialogContent
          closeVisible={closeVisible}
          className={twMerge("max-h-[95dvh] overflow-auto", className)}
        >
          <DialogHeader className="w-full max-w-full text-center space-y-4 items-center">
            {icon && (
              <div
                className={cn(
                  "h-16 w-16 mb-2 rounded-full overflow-hidden items-center justify-center flex shrink-0",
                  classNameIcon,
                )}
              >
                {icon}
              </div>
            )}
            <DialogTitle
              className={clsx(
                "text-center self-stretch w-full min-w-0 max-w-full leading-snug [overflow-wrap:anywhere]",
                {
                  "mt-10": !icon,
                },
              )}
            >
              {title}
            </DialogTitle>
            {description && (
              <DialogDescription className="text-center text-muted">
                {description}
              </DialogDescription>
            )}
          </DialogHeader>
          {children}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer {...props}>
      {trigger && <DrawerTrigger asChild={asChild}>{trigger}</DrawerTrigger>}
      <DrawerContent>
        <DrawerHeader className="text-left gap-2 flex items-start">
          {icon && (
            <div className="h-16 w-16 mb-2 border-2 rounded-full overflow-hidden items-center justify-center flex shrink-0">
              {icon}
            </div>
          )}
          <div className="min-w-0 flex-1 text-left">
            <DrawerTitle className="w-full min-w-0 max-w-full leading-snug [overflow-wrap:anywhere]">
              {title}
            </DrawerTitle>
            {description && (
              <DrawerDescription className="text-left text-muted">
                {description}
              </DrawerDescription>
            )}
          </div>
        </DrawerHeader>
        {/* DrawerContent is anchored to the bottom of the viewport, so an
            uncapped body grows upward and renders its tail — often the confirm
            button — off the top of the screen with no way to scroll to it.
            Cap and scroll like DrawerDialogBase and DrawerDialog do. */}
        <div
          className="px-2 md:px-4 space-y-4 overflow-y-auto"
          style={{
            maxHeight: height - height / 3,
          }}
        >
          {children}
        </div>
        {closeVisible && (
          <DrawerFooter className="pt-2 mt-4">
            <DrawerClose asChild>
              <div>
                <Button variant="outline">{t("Cancel")}</Button>
              </div>
            </DrawerClose>
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
};
export const ConfirmRemoveDrawerDialog = ({
  title,
  message,
  confirmText,
  onConfirm,
  loading,
  icon = (
    <div className="h-full w-full bg-red-100 flex items-center justify-center">
      <TrashIcon className="h-6 w-6 text-destructive" aria-hidden="true" />
    </div>
  ),
  children,
  trigger,
}: {
  trigger: React.ReactNode;
  title: string;
  message: string;
  confirmText?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  loading: boolean;
  onConfirm(): void;
}) => {
  const { t } = useTStream("common");
  const [open, setOpen] = React.useState(false);
  const [confirmInput, setConfirmInput] = React.useState("");

  const beforeConfirm = React.useCallback(async () => {
    hapticWarning();
    await onConfirm();
    setOpen(false);
  }, [onConfirm]);

  const isConfirmDisabled = confirmText
    ? confirmInput.toLowerCase() !== confirmText.toLowerCase()
    : false;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isConfirmDisabled) {
      beforeConfirm();
    }
  };

  return (
    <DrawerDialogStacked
      icon={icon}
      title={title}
      description={message}
      trigger={trigger}
      onOpenChange={setOpen}
      open={open}
    >
      {children}
      {confirmText && (
        <Input
          type="text"
          value={confirmInput}
          onChange={(e) => setConfirmInput(e.target.value)}
          onKeyDown={handleKeyDown}
          id="confirm-input"
          placeholder={`${t("Type")} "${confirmText}" ${t("to remove")}`}
        />
      )}

      <div className="flex w-full gap-2 md:space-x-4 mt-3 px-5">
        <Button
          onClick={() => setOpen(false)}
          variant="outline"
          className="w-full"
        >
          {t("Cancel")}
        </Button>
        <Button
          onClick={beforeConfirm}
          loading={loading}
          disabled={isConfirmDisabled}
          className="w-full"
        >
          {t("Remove")}
        </Button>
      </div>
    </DrawerDialogStacked>
  );
};
